import { useCallback, useEffect, useState } from 'react';
import { request } from '../utils/api-client';
import { AppRoutes } from './routes';
import { getTenantApiPath } from './routing';
import { withRequiredTelegramAuth } from './telegramAuth';
import { useTelegramAppTheme } from './hooks/useTelegramAppTheme';
import { StaffRolesProvider, type StaffRolesMap } from './context/StaffRolesContext';
import { DiscoverySessionProvider } from './context/DiscoverySessionContext';

interface ServicesResponse {
  data?: unknown;
  staffRoles?: unknown;
}

function normalizeServices(input: unknown): string[] {
  if (!Array.isArray(input)) {
    return [];
  }

  const normalized = input
    .filter((value): value is string => typeof value === 'string')
    .map((service) => service.trim())
    .filter((service) => service.length > 0);

  return Array.from(new Set(normalized));
}

function normalizeStaffRoles(input: unknown): StaffRolesMap {
  if (!input || typeof input !== 'object' || Array.isArray(input)) {
    return {};
  }

  const rolesByService = input as Record<string, unknown>;
  const normalized: StaffRolesMap = {};

  for (const [service, rawRoleMap] of Object.entries(rolesByService)) {
    if (!rawRoleMap || typeof rawRoleMap !== 'object' || Array.isArray(rawRoleMap)) {
      continue;
    }

    const roleEntries = Object.entries(rawRoleMap as Record<string, unknown>)
      .filter(([, value]) => typeof value === 'boolean')
      .map(([role, value]) => [role, value] as const);

    if (roleEntries.length === 0) {
      continue;
    }

    normalized[service] = Object.fromEntries(roleEntries) as Record<string, boolean>;
  }

  return normalized;
}

export default function App() {
  useTelegramAppTheme();

  const [activeServices, setActiveServices] = useState<string[]>([]);
  const [staffRoles, setStaffRoles] = useState<StaffRolesMap>({});
  const [servicesLoading, setServicesLoading] = useState(true);
  const [servicesError, setServicesError] = useState<string | null>(null);
  const [servicesAttempt, setServicesAttempt] = useState(0);

  const retryServices = useCallback(() => {
    setServicesAttempt((current) => current + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadServices = async () => {
      setServicesLoading(true);
      setServicesError(null);

      try {
        const response = await request<ServicesResponse>(
          getTenantApiPath('/services'),
          withRequiredTelegramAuth(),
        );

        if (!cancelled) {
          setActiveServices(normalizeServices(response.data));
          setStaffRoles(normalizeStaffRoles(response.staffRoles));
        }
      } catch {
        if (!cancelled) {
          setServicesError('Не удалось загрузить список сервисов. Попробуйте снова.');
          setActiveServices([]);
          setStaffRoles({});
        }
      } finally {
        if (!cancelled) {
          setServicesLoading(false);
        }
      }
    };

    void loadServices();

    return () => {
      cancelled = true;
    };
  }, [servicesAttempt]);

  return (
    <StaffRolesProvider roles={staffRoles}>
      <DiscoverySessionProvider>
        <AppRoutes
          activeServices={activeServices}
          staffRoles={staffRoles}
          servicesLoading={servicesLoading}
          servicesError={servicesError}
          retryServices={retryServices}
        />
      </DiscoverySessionProvider>
    </StaffRolesProvider>
  );
}
